/**
 * @fileoverview `mountMarketingRoot` — the ONE marketing entry mount (LIFE-SHORTLISTED-001, coordinator
 * ruling Q-LC24-7). Each marketing site's `main.jsx` carried its own copy of the hydrate-or-render
 * branch; they call this now.
 *
 * SERVER MARKUP IS HYDRATED. A document carrying a server-render marker — the build-time prerender's
 * (MSSG-001) or a blog page's seed (BLOG-STD-001) — is hydrated, never re-rendered: a fresh
 * `createRoot` over server markup discards the whole prerendered tree, and the visitor watches the
 * page appear and vanish.
 *
 * IN THE LANGUAGE IT WAS RENDERED IN. The server renders one language and says which in
 * `<html lang>`. The client's i18n resolves the VISITOR's language when its module loads (stored
 * preference → browser → default), so a Spanish-preferring visitor on an English prerendered page
 * hydrated Spanish text over English markup: React #418 on every page, and React then threw the
 * server DOM away and rendered the page again (measured live on www.paydaypuffin.com with an
 * `es-ES` browser, 2026-09-27). The mount hydrates in the document's language and moves to the
 * visitor's once hydration has committed — the same page the visitor saw before, without the error
 * and without discarding the server's DOM.
 *
 * Both moves use the i18n INSTANCE's own `changeLanguage`, never the bootstrap's persisting wrapper:
 * hydrating in the document's language is not the visitor choosing it, so their stored preference
 * is left exactly as it was.
 *
 * AND IT REPORTS WHAT REACT CATCHES (MUTATION-INSTRUMENT-001 F-TOCONTAIN-3 U2b). Both roots take
 * `createReactRootErrorOptions(logger)` from `@puffin-core/logging-browser`: a render error a
 * boundary catches (`onCaughtError`) or a hydration mismatch React recovers from
 * (`onRecoverableError`) otherwise defaults to `console.error` and reaches no backend. `logger` is
 * the package singleton, which the entry's `./telemetry` bootstrap configured before this module
 * ran. Every marketing entry mounts through here, so the option lives here once; the
 * `reactRootErrorsReported` contract judges this module and counts each entry's call.
 *
 * AND IT RECOVERS (F-TOCONTAIN-3 U2b-2). Both trees are wrapped in page-shell's ONE app-level
 * `<AppErrorBoundary>` (the marketing-safe `/app-error-boundary` subpath), so a render error on a
 * public page shows a recovery screen instead of a blank one. The boundary renders no DOM of its
 * own while nothing has failed, so the hydrated tree still matches the server's markup; the
 * `appErrorBoundaryMounted` contract judges this module the same way.
 *
 * AND IT LEARNS OF A NEW BUILD (WEB-APP-VERSION-REFRESH-001 U4d). `mountBuildUpdateBand` (auth-shell) gives the page
 * the update band in a React root of its OWN, in a container before `#root`: a public page left open is told a newer
 * build is ready and reloads only at a safe moment (a contact form being typed holds it back). Its own root, because
 * a sibling in the hydrated tree changed every `useId` and mismatched the server's markup; `buildUpdateNoticeMounted`
 * judges this module.
 *
 * @module @puffin-core/marketing-shell/factories/mountMarketingRoot
 */

import { StrictMode, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createReactRootErrorOptions, logger } from '@puffin-core/logging-browser';
import { AppErrorBoundary } from '@puffin-core/page-shell/app-error-boundary';
import { mountBuildUpdateBand } from '@puffin-core/auth-shell/build-update-band';

/** The data blocks a server render leaves in the document (MSSG-001 and BLOG-STD-001). */
export const SERVER_MARKUP_MARKER_IDS = Object.freeze(['__marketing-ssg__', '__blog-ssr-seed__']);

/**
 * Runs `onHydrated` once the hydrated tree has committed.
 *
 * @param {Object} props
 * @param {() => void} props.onHydrated
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode}
 */
function AfterHydration({ onHydrated, children }) {
  useEffect(() => {
    onHydrated();
  }, [onHydrated]);
  return children;
}

AfterHydration.propTypes = {
  onHydrated: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * @param {Object} args
 * @param {HTMLElement} args.rootElement - the `#root` element
 * @param {React.ReactElement} args.app - the marketing app element
 * @param {import('i18next').i18n} args.i18n - the app's i18n instance
 * @returns {import('react-dom/client').Root} the React root
 */
export function mountMarketingRoot({ rootElement, app, i18n }) {
  // Before either tree: the band's own root, so neither the fresh nor the hydrated tree changes shape.
  mountBuildUpdateBand({ beside: rootElement, rootOptions: createReactRootErrorOptions(logger) });
  const hasServerMarkup = SERVER_MARKUP_MARKER_IDS.some((id) => document.getElementById(id));
  if (!hasServerMarkup) {
    const root = createRoot(rootElement, createReactRootErrorOptions(logger));
    root.render(
      <StrictMode>
        <AppErrorBoundary>{app}</AppErrorBoundary>
      </StrictMode>,
    );
    return root;
  }

  const documentLanguage = document.documentElement.lang;
  const visitorLanguage = i18n.language;
  const switchAfterHydration = Boolean(documentLanguage) && documentLanguage !== visitorLanguage;
  if (switchAfterHydration) {
    i18n.changeLanguage(documentLanguage);
  }
  const onHydrated = () => {
    if (switchAfterHydration) {
      i18n.changeLanguage(visitorLanguage);
    }
  };

  return hydrateRoot(
    rootElement,
    <StrictMode>
      <AppErrorBoundary>
        <AfterHydration onHydrated={onHydrated}>{app}</AfterHydration>
      </AppErrorBoundary>
    </StrictMode>,
    createReactRootErrorOptions(logger),
  );
}

export default mountMarketingRoot;
